import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import domtoimage from 'dom-to-image';
import { jsPDF } from "jspdf";
import { ResponsiveContainer, LineChart, XAxis, YAxis, ReferenceLine, Line, ReferenceDot, ReferenceArea } from 'recharts';
import { Box, Typography, CircularProgress, Snackbar } from '@material-ui/core';

import MobileLoading from '../../../components/MobileLoading';
import { MobileAlert } from '../../../components';
import { AppButton, ReverseAppButton } from '../components/InputComponents';
import RepLIChart from '../../desktop/repli/RepLIChart.component';

import { arrayOfNumbers } from '../../../services/util.service';

import useStyles from './MyRepLI.style';
import Cookies from 'universal-cookie';


const cookies = new Cookies();

const CustomLabel = props => {
	return (
		<svg width="30" height="30" x={props.viewBox.x + props.viewBox.width} y={props.viewBox.y - 15} xmlns="http://www.w3.org/2000/svg">
			<g>
				<title>Layer 1</title>
				<path
					fill="#ffff00"
					stroke="#ffffaa"
					d="m2.5,10.90327l8.40332,0l2.59669,-8.40327l2.59669,8.40327l8.40331,0l-6.79841,5.19345l2.59682,8.40327l-6.79842,-5.19359l-6.79841,5.19359l2.59682,-8.40327l-6.79841,-5.19345z"
					id="svg_1"
					strokeWidth="2"
				/>
			</g>
		</svg>
	);

};

const DotShape = props => {
	const { cx, cy, color } = props;

	return (
		<svg x={cx} y={cy} height="20" width="20" overflow="visible">
			<circle r="10" style={{ fill: color }} />
		</svg>
	)
};

const MyRepLI = ({scrollToTop}) => {
	const classes = useStyles();

	const [loading, setLoading] = useState(true);

	const [profile, setProfile] = useState();
	const [chartData, setChartData] = useState();
	const [liveBirthChartDots, setLiveBirthChartDots] = useState([]);
	const [adoptionChartDots, setAdoptionChartDots] = useState([]);
	const [stepchildrenChartDots, setStepchildrenChartDots] = useState([]);
	// const [childrenLossChartDots, setChildrenLossChartDots] = useState([]);
	const [downloading, setDownloading] = useState(false);
	const [sending, setSending] = useState(false);
	const [result, setResult] = useState();
	const [chartDataValid, setchartDataValid] = useState(false);
	const [openFullChart, setOpenFullChart] = useState(false);
	const [fullChartLabel, setFullChartLabel] = useState('Open Full Chart');

	//Get patient profile
	useEffect(() => {
		const getHealthProfile = async () => {
			try {
				let response = await fetch(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_PATIENT_PROFILE}`, {
					headers: {
						'X-ACCESS-TOKEN': cookies.get('token')
					}
				});
				if (!response) return;
				setProfile(await response.json());
			} catch (error) {
				console.log(error);
			} finally {
				setLoading(false);
			};
		};

		getHealthProfile();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	//Calculate chart line graph data
	useEffect(() => {
		if (!profile) return;

		let birthYear = new Date(profile.dateOfBirth).getFullYear();
		let currentAge = getCurrentAgeFromBirthYear(birthYear);
		let ageFrame = arrayOfNumbers(parseInt(profile.firstPeriodAge), currentAge - profile.firstPeriodAge + 1);

		let liveBirthPregnancies = profile.pregnancies && profile.pregnancies
			.filter(pregnancy =>
				pregnancy.outcome === 'Live Birth'
				&& (pregnancy.liveBirthQty - pregnancy.stillbirthQty) > 0
			);
		setChartData(
			ageFrame.map(age => {
				let pregnanciesBeforeAge = liveBirthPregnancies
					.filter(pregnancy => pregnancy.year <= birthYear + age);
				let totalLiveBirth = pregnanciesBeforeAge
					.reduce((a, b) => a + (b.liveBirthQty || 0), 0);
				let totalPlacedAdotion = pregnanciesBeforeAge.reduce((a, b) => a + (b.placedAdoptionQty || 0), 0);

				let adoptionsBeforeYear = profile.adoptions
					.filter(adoption => adoption.year <= birthYear + age);
				let totalAdoption = adoptionsBeforeYear.reduce((a, b) => a + (b.childrenQty || 0), 0);

				let stepchildrenBeforeYear = profile.stepchildren
					.filter(stepchildren => stepchildren.year <= birthYear + age);
				let totalStepchildren = stepchildrenBeforeYear.reduce((a, b) => a + (b.stepchildrenQty || 0), 0);

				let childrenLossBeforeYear = profile.childrenLoss
					.filter(childrenLoss => childrenLoss.year <= birthYear + age);
				let totalChildrenLoss = childrenLossBeforeYear.reduce((a, b) => a + (b.childrenQty || 0), 0);

				let relativeAccumulatedOutcomeQty = (totalLiveBirth - totalPlacedAdotion + totalAdoption + totalStepchildren - totalChildrenLoss);

				return { age: age, childrenQty: relativeAccumulatedOutcomeQty }
			})
		);
	}, [profile]);

	//Calculate the reference dots
	useEffect(() => {
		if (!profile) return;

		let birthYear = new Date(profile.dateOfBirth).getFullYear();
		let liveBirthPregnancies =profile.pregnancies && profile.pregnancies
			.filter(pregnancy =>
				pregnancy.outcome === 'Live Birth'
				&& pregnancy.liveBirthQty > 0
			);

		setLiveBirthChartDots(liveBirthPregnancies && liveBirthPregnancies.flatMap(pregnancy => {
			// let fqpLineYValueForYear = pregnancy.liveBirthQty;
			let fqpLineYValueForYear = chartData
				.filter(data => data.age === pregnancy.year - birthYear)
				.reduce((a, b) => a + (b.childrenQty || 0), 0);

			return (
				fqpLineYValueForYear > 0
					? [{ age: pregnancy.year - birthYear, cumulativeChildrenQty: fqpLineYValueForYear }]
					: []
			)
		}));

		setAdoptionChartDots(profile.adoptions && profile.adoptions.flatMap(adoption => {
			// let fqpLineYValueForYear = adoption.childrenQty;
			let fqpLineYValueForYear = chartData
				.filter(data => data.age === adoption.year - birthYear)
				.reduce((a, b) => a + (b.childrenQty || 0), 0);
			return (
				fqpLineYValueForYear > 0
					? [{ age: adoption.year - birthYear, cumulativeChildrenQty: fqpLineYValueForYear }]
					: []
			);
		}));

		setStepchildrenChartDots(profile.stepchildren && profile.stepchildren.flatMap(stepchild => {
			// let fqpLineYValueForYear = stepchild.stepchildrenQty;
			let fqpLineYValueForYear = chartData
				.filter(data => data.age === stepchild.year - birthYear)
				.reduce((a, b) => a + (b.childrenQty || 0), 0);
			return (
				fqpLineYValueForYear > 0
					? [{ age: stepchild.year - birthYear, cumulativeChildrenQty: fqpLineYValueForYear }]
					: []
			);
		}));

		// setChildrenLossChartDots(profile.childrenLoss.flatMap(loss => {
		// 	// let fqpLineYValueForYear = loss.childrenQty;
		// 	let fqpLineYValueForYear = chartData
		// 		.filter(data => data.age === loss.year - birthYear)
		// 		.reduce((a, b) => a + (b.childrenQty || 0), 0);
		// 	return (
		// 		fqpLineYValueForYear > 0
		// 			? [{ age: loss.year - birthYear, cumulativeChildrenQty: fqpLineYValueForYear }]
		// 			: []
		// 	);
		// }));
		if (chartData.length !== 0) return setchartDataValid(true);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chartData]);

	const getCurrentAgeFromBirthYear = birthYear => {
		return new Date(Date.now()).getFullYear() - birthYear;
	};

	const getChartPDF = async () => {
		let chartNode = document.getElementById('repliChart')
		let repliImage = await domtoimage.toPng(chartNode);

		var repliImageHeight = chartNode.clientHeight;
		var repliImageWidth = chartNode.clientWidth;

		let margin = 10;
		let imgWidth = 210 - 2 * margin;
		let pageHeight = 295;
		let imgHeight = repliImageHeight * imgWidth / repliImageWidth;
		let heightLeft = imgHeight;
		let position = 0;

		let pdf = new jsPDF('p', 'mm', 'a4');

		pdf.setFontSize(22);
		pdf.text(`My RepLI`, 10, 10);

		pdf.addImage(repliImage, 'PNG', margin, 20, imgWidth, imgHeight);
		pdf.setFontSize(16);
		pdf.setTextColor('#FF0000');

		heightLeft -= pageHeight;

		while (heightLeft >= 0) {
			position = heightLeft - imgHeight;
			pdf.addPage();
			pdf.addImage(repliImage, 'PNG', margin, position, imgWidth, imgHeight);
			heightLeft -= pageHeight;
		}

		return pdf;
	}

	const handleDownloadButtonClick = async e => {
		e.preventDefault();
		setDownloading(true);
		try {
			let pdf = await getChartPDF();
			pdf.save('myrepli.pdf');

		} catch (error) {
			setResult({ severity: 'error', message: error.message });
			console.log(error);
		} finally {
			setDownloading(false);
		}
	};

	const handleOpenFullChartButtonClick = async e => {
		if (openFullChart) {
			setOpenFullChart(false);
			setFullChartLabel('Open Full Chart');
		}
		else {
			setOpenFullChart(true);
			setFullChartLabel('Close Full Chart');
		}
	}

	const handleSendButtonClick = async e => {
		e.preventDefault();
		setSending(true);
		try {
			let pdf = await getChartPDF();
			let payload = new FormData()
			payload.append('pdf', pdf.output('blob', 'myrepli.pdf'));
			let response = await fetch(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_PATIENT}/mailRepLI`, {
				method: 'post',
				body: payload,
				headers: {
					'X-ACCESS-TOKEN': cookies.get('token')
				}
			});

			setResult(
				response.ok
					? { severity: 'success', message: 'RepLI sent successfully' }
					: { severity: 'error', message: 'Sorry, something went wrong and we counldn\'t send the email' }
			);
		} catch (error) {
			console.log(error);
			setResult({ severity: 'error', message: 'Sorry, something went wrong and we counldn\'t send the email' });
		} finally {
			setSending(false);
		}
	};

	const handleMessageClose = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}

		setResult();
	};

	if (loading) return <MobileLoading />

	return (
		<Box className={classes.root}>
			<Box className={classes.title}>
				<Typography variant="h3">My RepLI</Typography>
			</Box>
			{
				chartDataValid && !openFullChart &&
				<>
					<Box className={classes.graphContainer}>
						<ResponsiveContainer
							height={250}
							width={'100%'}
						>
							<LineChart
								data={chartData}
								margin={{ top: 5, right: 80, left: 0, bottom: 5, }}
							>
								<XAxis
									dataKey="age"
									label={{ value: "Age", position: 'insideBottom', offset: 0, stroke: '#4A618A' }}
									stroke="#4A618A"
								/>
								<YAxis
									dataKey="childrenQty"
									label={{ value: 'Current Children', angle: -90, stroke: '#4A618A' }}
									allowDecimals={false}
									stroke="#4A618A"
									domain={[0, 'dataMax + 2']}
									tickCount={10}
								/>
								{profile &&
									<ReferenceArea
										y2={profile.desiredChildrenQty}
										fill="#A0F8BD" fillOpacity={0.6}
									/>
								}
								{profile &&
									<ReferenceArea y1={profile.desiredChildrenQty} fill="#612F6F" fillOpacity={0.6} />
								}
								<ReferenceLine
									y={profile.desiredChildrenQty}
									label={<CustomLabel />}
									stroke="yellow"
									strokeWidth={3}
								/>
								<Line dataKey="childrenQty" dot={false} stroke="purple" strokeWidth={3} />
								{liveBirthChartDots.length && liveBirthChartDots.map((dot, i) =>
									<ReferenceDot key={`livebirth${i}`} x={dot.age} y={dot.cumulativeChildrenQty} shape={<DotShape color="lime" />} />
								)}
								{adoptionChartDots.length && adoptionChartDots.map((dot, i) =>
									<ReferenceDot key={`adoption${i}`} x={dot.age} y={dot.cumulativeChildrenQty} shape={<DotShape color="lime" />} />
								)}
								{stepchildrenChartDots.length && stepchildrenChartDots.map((dot, i) =>
									<ReferenceDot key={`stepchildren${i}`} x={dot.age} y={dot.cumulativeChildrenQty} shape={<DotShape color="lime" />} />
								)}
								{/* {childrenLossChartDots.length && childrenLossChartDots.map((dot, i) =>
									<ReferenceDot key={`childrenloss${i}`} x={dot.age} y={dot.cumulativeChildrenQty} shape={<DotShape color="red" />} />
								)} */}
							</LineChart>
						</ResponsiveContainer>
					</Box>
				</>
			}
			{
				chartDataValid && openFullChart &&
				<div className={classes.openChart}>
					{
						profile &&
						<RepLIChart profile={profile} />
					}
				</div>
			}
			{
				!chartDataValid && !openFullChart &&
				<>
					<div className={classes.title} style={{ marginTop: 20 }}>
						<Typography variant="h5" color="error">Your profile was incomplete. Fill your profile to access your RepLI.</Typography>
						<Typography variant="h6" color="error">Please fill your profile.</Typography>
					</div>
				</>
			}
			<Box className={classes.footer}>
				<ReverseAppButton
					component={Link}
					variant="outlined"
					color="secondary"
					fullWidth
					size="large"
					onClick={handleOpenFullChartButtonClick}
				>
					{fullChartLabel}
				</ReverseAppButton>
				<ReverseAppButton
					variant="outlined"
					color="secondary"
					fullWidth
					size="large"
					disabled={downloading}
					onClick={handleDownloadButtonClick}
				>
					Download Chart
					{downloading && <CircularProgress size={24} className={classes.buttonProgress} />}
				</ReverseAppButton>
				{/* <ReverseAppButton
					variant="outlined"
					color="secondary"
					fullWidth
					size="large"
					onClick={handleSendButtonClick}
				>
					Email Full Chart
					{sending && <CircularProgress size={24} className={classes.buttonProgress} />}
				</ReverseAppButton> */}
				<AppButton
					component={Link}
					to="/app/dashboard"
					variant="contained"
					color="secondary"
					fullWidth
					size="large"
					onClick={scrollToTop}
				>
					Back To Dashboard
				</AppButton>
			</Box>
			<Snackbar open={result !== undefined} autoHideDuration={6000} onClose={handleMessageClose}>
				<MobileAlert onClose={handleMessageClose} severity={result && result.severity}>
					{result && result.message}
				</MobileAlert>
			</Snackbar>
			<div className={classes.hiddenChart}>
				<div id="repliChart">
					{
						profile &&
						<RepLIChart profile={profile} />
					}
				</div>
			</div>
		</Box >
	);
};

export default MyRepLI;
